import {isPlainObject} from 'lodash';
import {updateAt} from '@packages/utilities/array';
import {
  identity,
  emptyArray,
  compose,
  constantNone,
} from '@packages/utilities/fp/utils';
import {lookup} from '@packages/utilities/key-map';
import {maybe, Some, type Option} from '@packages/utilities/fp/option';
import {Result, Err, Ok} from '@packages/utilities/fp/result';

export type Json =
  | null
  | boolean
  | number
  | string
  | Array<Json>
  | {
      [key: string]: Json;
    };

export type JsonPath = Array<string | number>;

type JsonMapping<a> = {
  Null: (_: null) => a;
  Boolean: (b: boolean) => a;
  Number: (n: number) => a;
  String: (s: string) => a;
  Array: (arr: Array<Json>) => a;
  Object: (obj: {[key: string]: Json}) => a;
};

export const arrayToJson: (arr: Array<Json>) => Json = identity;
export const objectToJson: (obj: {[key: string]: Json}) => Json = identity;

export const matchJson =
  <a>(mapping: JsonMapping<a>): ((json: Json) => a) =>
  (json: Json) => {
    if (json === null) {
      return mapping.Null(json);
    }
    if (typeof json === 'boolean') {
      return mapping.Boolean(json);
    }
    if (typeof json === 'number') {
      return mapping.Number(json);
    }
    if (typeof json === 'string') {
      return mapping.String(json);
    }
    if (Array.isArray(json)) {
      return mapping.Array(json);
    }
    return mapping.Object(json);
  };

const onObject = matchJson<Option<{[k: string]: Json}>>({
  Null: constantNone,
  Boolean: constantNone,
  Number: constantNone,
  String: constantNone,
  Array: constantNone,
  Object: Some,
});

/**
 * Safely parses string to Json, returning undefined in case of an error
 */
export const parse = (string: string): undefined | Json => {
  try {
    return JSON.parse(string);
  } catch {
    return undefined;
  }
};

/**
 * Safely serializes a Json value
 */
export const stringify: (arg1: Json) => Result<Error, string> = (value) => {
  try {
    return Ok(JSON.stringify(value));
  } catch (err: any) {
    return Err(err);
  }
};

export const path = (keys: Array<string>): ((json: Json) => Option<Json>) =>
  keys.length
    ? (json: Json) =>
        onObject(json)
          // @ts-expect-error - TS2345 - Argument of type 'string | undefined' is not assignable to parameter of type 'string'.
          .chain(lookup(keys[0]))
          .chain(path(keys.slice(1)))
    : Some;

const makeErrorHandler =
  (expectedType: string) =>
  <a>(value: Json): Result<string, a> =>
    Err(`Expected a Json ${expectedType} but got ${JSON.stringify(value)}.`);

const nonNullError = makeErrorHandler('null');
export const expectNull = matchJson<Result<string, null>>({
  Null: Ok,
  Boolean: nonNullError,
  Number: nonNullError,
  String: nonNullError,
  Array: nonNullError,
  Object: nonNullError,
});

const nonBooleanError = makeErrorHandler('boolean');
export const expectBoolean = matchJson<Result<string, boolean>>({
  Null: nonBooleanError,
  Boolean: Ok,
  Number: nonBooleanError,
  String: nonBooleanError,
  Array: nonBooleanError,
  Object: nonBooleanError,
});

const nonNumberError = makeErrorHandler('number');
export const expectNumber = matchJson<Result<string, number>>({
  Null: nonNumberError,
  Boolean: nonNumberError,
  Number: Ok,
  String: nonNumberError,
  Array: nonNumberError,
  Object: nonNumberError,
});

const nonStringError = makeErrorHandler('string');
export const expectString = matchJson<Result<string, string>>({
  Null: nonStringError,
  Boolean: nonStringError,
  Number: nonStringError,
  String: Ok,
  Array: nonStringError,
  Object: nonStringError,
});

const nonArrayError = makeErrorHandler('array');
export const expectArray = matchJson<Result<string, Array<Json>>>({
  Null: nonArrayError,
  Boolean: nonArrayError,
  Number: nonArrayError,
  String: nonArrayError,
  Array: Ok,
  Object: nonArrayError,
});

const nonObjectError = makeErrorHandler('object');
export const expectObject = matchJson<Result<string, {[k: string]: Json}>>({
  Null: nonObjectError,
  Boolean: nonObjectError,
  Number: nonObjectError,
  String: nonObjectError,
  Array: nonObjectError,
  Object: Ok,
});

export const expectProp = (key: string) =>
  compose(maybe<Result<string, Json>>(Err(`Missing key ${key}`))(Ok<Json>))(
    lookup(key)<Json>
  );

const giveUndefined = (
  _:
    | null
    | number
    | string
    | boolean
    | {
        [key: string]: Json;
      }
    | Array<Json>
) => undefined;

export const get = (json: Json, jsonPath: JsonPath): Json | undefined => {
  if (jsonPath.length < 1) return json;

  return matchJson({
    Null: giveUndefined,
    Boolean: giveUndefined,
    Number: giveUndefined,
    String: giveUndefined,
    Array: function matchArray(arr) {
      const [index, ...nextPath] = jsonPath;
      if (typeof index !== 'number' || index < 0 || index >= arr.length)
        return undefined;
      // @ts-expect-error - TS2345 - Argument of type 'Json | undefined' is not assignable to parameter of type 'Json'.
      return get(arr[index], nextPath);
    },
    Object: function matchObject(obj) {
      const [key, ...nextPath] = jsonPath;
      if (typeof key !== 'string' || !obj.hasOwnProperty(key)) return undefined;
      // @ts-expect-error - TS2345 - Argument of type 'Json | undefined' is not assignable to parameter of type 'Json'.
      return get(obj[key], nextPath);
    },
  })(json);
};

export const set = (json: Json, jsonPath: JsonPath, value: Json): Json => {
  if (jsonPath.length < 1) return value;
  const giveBackOriginal = (_: null | number | string | boolean) => json;

  return matchJson({
    Null: giveBackOriginal,
    Boolean: giveBackOriginal,
    Number: giveBackOriginal,
    String: giveBackOriginal,
    Array: function matchArray(arr) {
      const [index, ...nextPath] = jsonPath;
      if (typeof index !== 'number') return json;
      return updateAt(arr, index, (item) => set(item, nextPath, value));
    },
    Object: function matchObject(obj) {
      const [key, ...nextPath] = jsonPath;
      if (typeof key !== 'string' || !obj.hasOwnProperty(key)) return json;
      const oldVal = obj[key];
      // @ts-expect-error - TS2345 - Argument of type 'Json | undefined' is not assignable to parameter of type 'Json'.
      const newVal = set(oldVal, nextPath, value);
      return newVal === oldVal ? json : {...obj, [key]: newVal};
    },
  })(json);
};

export const getNull: (arg1: Json) => undefined | null = matchJson({
  Null: identity,
  Boolean: giveUndefined,
  Number: giveUndefined,
  String: giveUndefined,
  Array: giveUndefined,
  Object: giveUndefined,
});

export const getBoolean: (arg1: Json) => undefined | boolean = matchJson({
  Null: giveUndefined,
  Boolean: identity,
  Number: giveUndefined,
  String: giveUndefined,
  Array: giveUndefined,
  Object: giveUndefined,
});

export const getNumber: (arg1: Json) => undefined | number = matchJson({
  Null: giveUndefined,
  Boolean: giveUndefined,
  Number: identity,
  String: giveUndefined,
  Array: giveUndefined,
  Object: giveUndefined,
});

export const getString: (arg1: Json) => undefined | string = matchJson({
  Null: giveUndefined,
  Boolean: giveUndefined,
  Number: giveUndefined,
  String: identity,
  Array: giveUndefined,
  Object: giveUndefined,
});

export const getArray: (arg1: Json) => undefined | Array<Json> = matchJson({
  Null: giveUndefined,
  Boolean: giveUndefined,
  Number: giveUndefined,
  String: giveUndefined,
  Array: identity,
  Object: giveUndefined,
});

export const getObject: (arg1: Json) =>
  | undefined
  | {
      [key: string]: Json;
    } = matchJson({
  Null: giveUndefined,
  Boolean: giveUndefined,
  Number: giveUndefined,
  String: giveUndefined,
  Array: giveUndefined,
  Object: identity,
});

/**
 * JavaScript doesn't have good support for recursion, therefore
 * we need to avoid true recursion in favor of iteration. This was
 * specifically changed from more simple recursive code to this
 * complex iterative logic due to Max Range Errors during E2E tests
 * after a Babel upgrade.
 *
 * The previous code to help explain what is happening here:
 *
 *  export const everywhere = (fn: Json => Json) => {
      const recurse = matchJson<Json>({
        Null: identity,
        Boolean: identity,
        Number: identity,
        String: identity,
        Array: arr => mapArray(go)(arr),
        Object: obj => mapValues(go)(obj),
      });

      const go = (json: Json): Json => recurse(fn(json));
      return go;
    };
 */
export const everywhere = (
  fn: (arg1: Json) => Json
): ((json: Json) => Json) => {
  function go(json: Json): Json {
    if (
      json === null ||
      typeof json === 'boolean' ||
      typeof json === 'number' ||
      typeof json === 'string'
    ) {
      return fn(json);
    }
    // Need to track parent references and copies to
    // build apply changes up the tree after
    // mutations observed
    const refMap = new WeakMap<
      | Array<Json>
      | {
          [key: string]: Json;
        },
      {
        key: string | number;
        parent:
          | Array<Json>
          | {
              [key: string]: Json;
            };
      }
    >();
    // map of original to copied objects
    const copyMap = new WeakMap<
      | Array<Json>
      | {
          [key: string]: Json;
        },
      | Array<Json>
      | {
          [key: string]: Json;
        }
    >();

    // begin iterative depth first mutations
    let rootResult = fn(json);
    let rootChanged = rootResult !== json;
    const stack = [rootResult];

    // add non-primative leaf nodes to the stack to be operated on
    function maybeAddToStack(
      x: Json,
      parent:
        | Array<Json>
        | {
            [key: string]: Json;
          },
      key: string | number
    ) {
      if (
        x !== null &&
        typeof x !== 'boolean' &&
        typeof x !== 'number' &&
        typeof x !== 'string'
      ) {
        refMap.set(x, {parent, key});
        stack.push(x);
      }
    }

    do {
      // pop nodes off the end of the stack for depth first
      const item = stack.pop();
      if (
        json === null ||
        typeof json === 'boolean' ||
        typeof json === 'number' ||
        typeof json === 'string'
      ) {
        continue;
      }

      let itemChanged = false;
      if (Array.isArray(item)) {
        const copy: Array<Json> = [];
        copyMap.set(item, copy);
        for (let index = 0; index < item.length; index++) {
          // @ts-expect-error - TS2345 - Argument of type 'Json | undefined' is not assignable to parameter of type 'Json'.
          const leaf = fn(item[index]);
          copy[index] = leaf;
          itemChanged = itemChanged || leaf !== item[index];
          maybeAddToStack(leaf, item, index);
        }
      } else if (
        item !== null &&
        typeof item !== 'boolean' &&
        typeof item !== 'number' &&
        typeof item !== 'string'
      ) {
        // we know it is an object at this point
        const copy: {[k: string]: Json} = {};
        // @ts-expect-error - TS2345 - Argument of type '{ [key: string]: Json; } | undefined' is not assignable to parameter of type 'Json[] | { [key: string]: Json; }'.
        copyMap.set(item, copy);
        for (const key in item) {
          if (item.hasOwnProperty(key)) {
            // @ts-expect-error - TS2345 - Argument of type 'Json | undefined' is not assignable to parameter of type 'Json'.
            const leaf = fn(item[key]);
            copy[key] = leaf;
            itemChanged = itemChanged || leaf !== item[key];
            maybeAddToStack(leaf, item, key);
          }
        }
      }

      if (itemChanged) {
        rootChanged = true;
        rootResult = copyMap.get(json) || rootResult;
        // loop from the current item setting the copy onto
        // the parent copies up the tree using iterative approach
        let itemToUpdate = item;
        let parent = null;
        do {
          // @ts-expect-error - TS2345 - Argument of type 'Json | undefined' is not assignable to parameter of type 'Json[] | { [key: string]: Json; }'.
          if (refMap.has(itemToUpdate)) {
            // @ts-expect-error - TS2345 - Argument of type 'Json | undefined' is not assignable to parameter of type 'Json[] | { [key: string]: Json; }'.
            const refObject = refMap.get(itemToUpdate);
            if (!refObject || !refObject.parent) break;
            parent = refObject.parent;
            const parentCopy = copyMap.get(parent);
            if (!parentCopy) break;
            // @ts-expect-error - TS2345 - Argument of type 'Json | undefined' is not assignable to parameter of type 'Json[] | { [key: string]: Json; }'.
            const itemCopy = copyMap.get(itemToUpdate);
            // has this item reference already been updated in the parentCopy? then break loop
            // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string | number' can't be used to index type 'Json[] | { [key: string]: Json; }'.
            if (parentCopy[refObject.key] === itemCopy) break;
            // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string | number' can't be used to index type 'Json[] | { [key: string]: Json; }'.
            parentCopy[refObject.key] = itemCopy;
            itemToUpdate = parent;
          }
          // if itemToUpdate is equal to the base node then exit
        } while (
          itemToUpdate &&
          itemToUpdate !== rootResult &&
          itemToUpdate !== json
        );
      }
    } while (stack.length > 0);

    return rootChanged ? rootResult : json;
  }

  return go;
};

export function getAsJson(data: unknown): undefined | Json {
  return getAsJsonImpl(data, emptyArray);
}

function getAsJsonImpl(
  data: unknown,
  encountered: Array<unknown>
): Json | undefined {
  if (data === null) return null;
  if (typeof data === 'string') return data;
  if (typeof data === 'boolean') return data;
  if (typeof data === 'number') return data;

  if (Array.isArray(data)) {
    if (encountered.includes(data)) {
      return;
    }

    const nextEncountered = encountered.concat([data]);

    for (const item of data) {
      const asJson = getAsJsonImpl(item, nextEncountered);
      if (asJson === undefined) return;
    }
    // Casting here to avoid creating a new array
    return data as Json;
  }
  if (typeof data === 'object' && isPlainObject(data)) {
    if (encountered.includes(data)) {
      return;
    }
    const nextEncountered = encountered.concat([data]);
    for (const key in data) {
      // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{}'.
      const asJson = getAsJsonImpl(data[key], nextEncountered);
      if (asJson === undefined) return;
    }
    // Casting here to avoid creating a new object
    return data as Json;
  }
}

/**
 * Removes all keys with `undefined` values from a Json object
 * and converts undefined -> null for items in arrays.
 *
 * This is equivalent of calling JSON.parse(JSON.stringify(obj))
 * but more performant.
 */
export function removeUndefined<T>(json: T): T {
  return matchJson<Json>({
    Null: identity,
    Boolean: identity,
    Number: identity,
    String: identity,
    Array: function matchArray(arr) {
      return arr.map((value) => {
        if (value === undefined) {
          // This transforms [...rest, undefined] to [...rest, null]
          // which is the same of what JSON.parse(JSON.stringify([...])) does
          return null;
        }
        return removeUndefined(value);
      });
    },
    Object: function matchObject(obj) {
      const newObj: {
        [key: string]: Json;
      } = {};
      Object.keys(obj).forEach((key) => {
        const value = obj[key];
        if (value !== undefined) {
          newObj[key] = removeUndefined(value);
        }
      });
      return newObj;
    },
  })(json as Json) as T;
}
